import { Paths, PathValue } from "@mctypes/global.types";
import { useEffect } from "react";
import { Button, Flex, Paper, Stack, Text, Title, Box } from "@mantine/core";
import { HSpace, McInputNew, McTextAreaNew } from "../../../../../../components";
import { IGKRapport, IGKTrykkfallIAvtrekksskapResultater } from "@mcapps/shared/reportSchemas/gkReportSchema";
import ReportDocxImageContainer from "../../../ReportBoard/ReportDocxImageContainer";
import { beregnetTverrsnittareal } from "../gkGlobalUtils";
const TrykkfallIAvtrekksskap: React.FC<{
  onUpdateEditData: <P extends Paths<IGKRapport, 4>>(path: P, value: PathValue<IGKRapport, P>) => void;
  data: IGKTrykkfallIAvtrekksskapResultater;
}> = ({ data, onUpdateEditData }) => {
  useEffect(() => {
    utdataBeregninger();
  }, [data.inndata.diameterPaaAvtrekkskanal]);
  const addNewRow = () => {
    const maaleresultaterArray = data.inndata.maaleresultaterMaalenipler;
    maaleresultaterArray.push(null);
    onUpdateEditData("testResultater.trykkfallIAvtrekksskap.inndata.maaleresultaterMaalenipler", maaleresultaterArray);
    return maaleresultaterArray;
  };
  const removeLastRow = () => {
    const updatedMaaleresultater = data.inndata.maaleresultaterMaalenipler;
    if (updatedMaaleresultater.length > 0) {
      updatedMaaleresultater.pop(); // Remove the last element
      onUpdateEditData(
        "testResultater.trykkfallIAvtrekksskap.inndata.maaleresultaterMaalenipler",
        updatedMaaleresultater,
      );
    }
  };
  const updateMaaleresultater = (resultat, i) => {
    const maaleresultater = [...data.inndata.maaleresultaterMaalenipler];
    maaleresultater[i] = resultat;
    onUpdateEditData("testResultater.trykkfallIAvtrekksskap.inndata.maaleresultaterMaalenipler", maaleresultater);
  };
  const utdataBeregninger = () => {
    const tverrsnittareal = beregnetTverrsnittareal(data.inndata.diameterPaaAvtrekkskanal);
    const utdata = {
      tverrsnittareal: tverrsnittareal,
    };
    onUpdateEditData("testResultater.trykkfallIAvtrekksskap.utdata", utdata);
  };
  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff">
      <Stack gap="xs">
        <Title order={4} fw={400}>
          Trykkfall i avtrekksskap
        </Title>
        <h5>Avtrekkskanal:</h5>
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", marginBottom: "1.5rem" }}>
          <McInputNew
            label="Antall målepunkter på avtrekkskanal (Minimum 4):"
            value={data.inndata.antallMaalepunkter}
            onBlur={(e) =>
              onUpdateEditData(
                "testResultater.trykkfallIAvtrekksskap.inndata.antallMaalepunkter",
                Number(e.target.value),
              )
            }
            normalizeNumbers={true}
            type="text"
            style={{ width: "25%" }}
          />
        </div>
        <Flex direction="column" mt="sm">
          <b>Måletiden er 60 sekunder &#40;s&#41;:</b>
        </Flex>
        <Flex direction="column" mt="sm" style={{ width: "50%" }}>
          <McTextAreaNew
            label="Beskrivelse av målepunkter:"
            value={data.inndata.beskrivelseAvMaalepunkter}
            onChange={(e) =>
              onUpdateEditData(
                "testResultater.trykkfallIAvtrekksskap.inndata.beskrivelseAvMaalepunkter",
                e.target.value,
              )
            }
          />
        </Flex>
        <Flex direction="column" mt="lg">
          <McInputNew
            label="Diameter på avtrekkskanal hvor målinger ble utført (m):"
            value={data.inndata.diameterPaaAvtrekkskanal}
            onBlur={(e) =>
              onUpdateEditData(
                "testResultater.trykkfallIAvtrekksskap.inndata.diameterPaaAvtrekkskanal",
                Number(e.target.value),
              )
            }
            normalizeNumbers={true}
            type="text"
            style={{ width: "25%" }}
          />
        </Flex>
        <Flex justify="space-between">
          <Text mb={0}>
            Tversnittsareal kanal &#40;m<sup>2</sup>&#41; ={" "}
            <b>{beregnetTverrsnittareal(data.inndata.diameterPaaAvtrekkskanal)?.toFixed(3) ?? ""}</b>
          </Text>
          <Text mb={0}>
            Formel: &#40;Ak = PI * &#40;D / 2&#41;<sup>2</sup>&#41;
          </Text>
        </Flex>
        <hr style={{ marginTop: 0 }} />
        <Flex direction="column" mt="lg">
          <McInputNew
            label="Måleresultat tilkoblingspunkt på avtrekkskanal (Pa):"
            value={data.inndata.maaleresultatTilkoblingspunkt}
            onBlur={(e) =>
              onUpdateEditData(
                "testResultater.trykkfallIAvtrekksskap.inndata.maaleresultatTilkoblingspunkt",
                Number(e.target.value),
              )
            }
            normalizeNumbers={true}
            type="text"
            style={{ width: "25%" }}
          />
        </Flex>
        <Box mt="lg">
          <h5>Målenippel:</h5>
          <b>Legg til innebygd målenippel:</b>
          <Flex>
            <Button color="green" onClick={addNewRow}>
              Legg til rad
            </Button>
            <HSpace />
            <Button variant="danger-filled" onClick={removeLastRow}>
              Fjern siste rad
            </Button>
          </Flex>
        </Box>
        {data.inndata.maaleresultaterMaalenipler.length > 0 && (
          <Flex direction="column" mt="lg">
            <b>Fyll inn måleresultater &#40;Pa&#41;:</b>
            {data.inndata.maaleresultaterMaalenipler.map((a, i) => (
              <McInputNew
                style={{ marginBottom: "0.25rem", width: "25%" }}
                key={i}
                value={a}
                normalizeNumbers={true}
                type="text"
                onBlur={(e) => updateMaaleresultater(Number(e.target.value), i)}
              />
            ))}
          </Flex>
        )}
        <Flex direction="column" mt="lg" style={{ marginBottom: "1.5rem" }}>
          <McInputNew
            label={
              <>
                {" "}
                Avtrekksluftmengde &#40;m<sup>3</sup>/s&#41;:{" "}
              </>
            }
            value={data.inndata.avtrekksLuftmengde}
            onBlur={(e) =>
              onUpdateEditData(
                "testResultater.trykkfallIAvtrekksskap.inndata.avtrekksLuftmengde",
                Number(e.target.value),
              )
            }
            normalizeNumbers={true}
            type="text"
            style={{ width: "25%" }}
          />
        </Flex>
        <div style={{ marginTop: "0.5rem" }}>
          <ReportDocxImageContainer docxImageTag="trykkfallIAvtrekksskap" title="Målepunkter" />
          <div style={{ width: "50%" }}>
            <McTextAreaNew
              label="Kommentar:"
              value={data.inndata.kommentar}
              onChange={(e) =>
                onUpdateEditData("testResultater.trykkfallIAvtrekksskap.inndata.kommentar", e.target.value)
              }
            />
          </div>
        </div>
      </Stack>
    </Paper>
  );
};
export default TrykkfallIAvtrekksskap;
